import React, { useState } from 'react';
import ConfirmCreditsModal from './ConfirmCreditsModal';

export const MovieActions = ({
    movie,
    user,
    isAnyProcessing,
    onAnalyzeFull,
    onAnalyzeDeepFocus,
    canUsePCR,
    onAnalyzePCR,
    onDeleteMovie,
    onRemoveFailedMovie,
    showSuccess
}) => {
    const [modal, setModal] = useState({ open: false, kind: null });

    const openPCR = () => setModal({ open: true, kind: 'pcr' });
    const openFull = () => setModal({ open: true, kind: 'full' });
    const openDeepFocus = () => setModal({ open: true, kind: 'deep_focus' });
    const close = () => setModal({ open: false, kind: null });

    const confirm = async () => {
        const kind = modal.kind;
        close();
        if (kind === 'pcr') await onAnalyzePCR(movie.id);
        if (kind === 'full') await onAnalyzeFull(movie.id);
        if (kind === 'deep_focus') await onAnalyzeDeepFocus(movie.id);
    };

    const buttons = [];

    console.log('MovieActions - Movie:', movie.id, 'Status:', movie.status, 'Source:', movie.source);

    // PCR Analysis button for uploaded movies
    if (movie.status === 'uploaded' && movie.source === 'server' && canUsePCR) {
        buttons.push(
            <button
                key="analyze_pcr"
                onClick={openPCR}
                disabled={isAnyProcessing}
                className="btn btn-outline-primary"
                title="Analyze PCR"
            >
                <i className="fa fa-film"></i> PCR
            </button>
        );
    }

    // Full Analysis buttons for successful uploads or PCR completed.
    // Split by edition: Standard (movie_credits) and Deep Focus / Deluxe
    // (movie_credits_deluxe). Both are shown to everyone; credits are checked
    // on confirm. Only the cheap PCR button above is gated by pcr_enabled.
    if ((movie.status === 'uploaded' || movie.status === 'full_pcr') && movie.source === 'server') {
        buttons.push(
            <button
                key="analyze"
                onClick={openFull}
                disabled={isAnyProcessing}
                className="btn btn-outline-primary"
                title="Full Analysis — Standard"
            >
                <i className="bi bi-search"></i> Full
            </button>
        );
        buttons.push(
            <button
                key="analyze_deep"
                onClick={openDeepFocus}
                disabled={isAnyProcessing}
                className="btn btn-outline-dark"
                title="Deep Focus — Deluxe (frontier models)"
            >
                <i className="bi bi-stars"></i> Deep Focus
            </button>
        );
    }

    // View results button
    if (movie.status === 'full_director' && movie.source === 'server') {
        buttons.push(
            <a
                key="view"
                href={`/disp?movieid=${movie.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary"
                title="View Results"
            >
                <i className="bi bi-eye"></i> View
            </a>
        );
    }

    // Delete button for server movies
    if (movie.source === 'server') {
        buttons.push(
            <button
                key="delete"
                onClick={() => onDeleteMovie(movie.id)}
                className="btn btn-outline-danger"
                title="Delete"
                disabled={isAnyProcessing}
            >
                <i className="bi bi-trash"></i>
            </button>
        );
    }

    // Remove button for failed uploads
    if (movie.source === 'failed') {
        buttons.push(
            <button
                key="remove"
                onClick={() => onRemoveFailedMovie(movie.id)}
                className="btn btn-outline-warning"
                title="Remove from list"
            >
                <i className="bi bi-x"></i> Remove
            </button>
        );
    }

    // Retry button for failed uploads (if originalFile exists)
    if (movie.source === 'failed' && movie.originalFile) {
        buttons.push(
            <button
                key="retry"
                onClick={() => {
                    // Re-queue the file for upload logic would go here
                    showSuccess(`Re-queued ${movie.filename} for upload`);
                }}
                className="btn btn-outline-info"
                title="Retry upload"
                disabled={isAnyProcessing}
            >
                <i className="bi bi-arrow-clockwise"></i> Retry
            </button>
        );
    }

    // Modal configuration — one entry per credit currency.
    const cost = 1;
    const modalConfig = {
        pcr: {
            balance: user?.pcr_credits ?? 0,
            title: 'Confirm PCR Credits',
            message: 'This will cost 1 PCR credit.',
        },
        full: {
            balance: user?.movie_credits ?? 0,
            title: 'Confirm Full Analysis (Standard) Credits',
            message: 'This will cost 1 Full Analysis (Standard) credit.',
        },
        deep_focus: {
            balance: user?.movie_credits_deluxe ?? 0,
            title: 'Confirm Deep Focus (Deluxe) Credits',
            message: 'This will cost 1 Deep Focus (Deluxe) credit.',
        },
    };
    const { balance, title, message } = modalConfig[modal.kind] ?? { balance: 0, title: '', message: '' };

    return (
        <>
            <div className="btn-group btn-group-sm" role="group">
                {buttons}
            </div>

            <ConfirmCreditsModal
                show={modal.open}
                onHide={close}
                title={title}
                message={message}
                cost={cost}
                balance={balance}
                confirmText="Confirm"
                onConfirm={confirm}
            />
        </>
    );
};